import React, { ChangeEvent, createRef } from "react";
import { IAdHocProject, IAsset, IProject } from "../../../../models/applicationState";
import { Button, Checkbox, FontIcon, PrimaryButton } from "@fluentui/react";
import { ContentSource } from "../../common/assetPreview/assetPreview";
import { AssetPreview } from "./assetPreview";

export interface IEditorSideBarItemProps {
    asset?: IAsset;
    style: any;
    getAssetCssClassNames: (asset: IAsset, selectedAsset?: IAsset) => string;
    selectedAsset?: IAsset;
    onAssetClicked: (asset: IAsset) => void;
    deleteAssetOnSelect: (event, asset: IAsset) => void;
    download: (url, name, event) => void;
    checkboxRowRenderer?: (asset: IAsset) => JSX.Element;
    renderBadges: (asset: IAsset) => JSX.Element;
    onAssetLoaded?: (asset: IAsset, ContentSource: ContentSource) => void;
    assetTitle?: string;
    projectId: string;
    adHocProject: IAdHocProject;
    loadAdHocOcrProject: (requestId: string) => Promise<void>;
    selectedRequestId?: string;
    hasModel?: boolean;
    hasRunningRequest?: boolean;
    isGenerateModelChecked?: boolean;
    onGenerateModelChanged: (key: string, val: string, checked: boolean) => void;
    toggleProjectToPost?(projectId: string, fileName: string, selected: boolean, generateModel: boolean);
}

export interface IEditorSideBarItemState {
    isVisible: boolean, 
    isChecked: boolean
}

export default class EditorSideBarItem extends React.Component<IEditorSideBarItemProps, IEditorSideBarItemState> {
    private panelRef: React.RefObject<any> = React.createRef();
    checkboxRef = createRef();

    constructor(props: IEditorSideBarItemProps) {
        super(props);

        this.state = {
            isVisible: true, 
            isChecked: false
        };
        this.initializeIntersectionObserver = this.initializeIntersectionObserver.bind(this);
        this.selectProjectToPost = this.selectProjectToPost.bind(this);

        
    }

    componentDidMount() {
        //this.initializeIntersectionObserver();
        if (this.props.adHocProject) {
            const assetName = Object.values(this.props.adHocProject.project.assets)[0].name;
            this.props.toggleProjectToPost(
                this.props.adHocProject.project.id,
                assetName,
                true,
                this.props.isGenerateModelChecked
            );
            this.setState({
                isChecked: true
            });
        }
        
    }

    initializeIntersectionObserver() {
        if (!('IntersectionObserver' in window)) {
            //console.error('IntersectionObserver not supported');
        }

        const intersectionObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
                entries.forEach((entry, i) => {
                    if (!entry.isIntersecting) return;

                    observer.unobserve(entry.target);

                    this.setState({
                        isVisible: true,
                    });
                });
            }
        );

        intersectionObserver.observe(this.panelRef.current);
    }

    private selectProjectToPost(e, assetName) {
        if (this.props.toggleProjectToPost) {
            const isChecked = e.target.checked;
            this.props.toggleProjectToPost(
                this.props.adHocProject.project.id,
                assetName,
                isChecked,
                this.props.isGenerateModelChecked
            );
            this.setState({
                isChecked: isChecked
            });
        }
    }

    public render() {
        const { style, getAssetCssClassNames, selectedAsset, onAssetClicked, deleteAssetOnSelect, download, checkboxRowRenderer, renderBadges, onAssetLoaded, assetTitle } = this.props;
        const asset = Object.values(this.props.adHocProject.project.assets)[0];
        let cssName = this.props.adHocProject.project.id === this.props.selectedRequestId ? 'asset-item-image-selected' : 'asset-item-image';
        const isSelected = this.props.adHocProject.project.id === this.props.selectedRequestId;
        let cbTitle = '';
        if (this.props.adHocProject) {
            if (this.props.adHocProject.hideGenerateModel) {
                cbTitle = "This feature is unavailable since vendor and document type is is not detected.";
            }
            else {
                if (this.props.adHocProject.hasRunningRequest) {
                    cbTitle = "This feature is temporary unavailable since there is a running request for new model";
                }
            }
        }

        return (            
            <div
                ref={this.panelRef}
                key={this.props.adHocProject.project.id}
                style={{
                    padding: "15px",
                    marginBottom: "10px",
                    border: isSelected ? "2px solid #0078d4" : "2px solid #cce7ff", // Blue border for selected and light blue for unselected
                    borderRadius: "8px", // Rounded corners for a modern look
                    backgroundColor: isSelected ? "#e6f7ff" : "#f4faff", // Light blue background for selected and lighter blue for unselected
                    boxShadow: isSelected
                        ? "0 4px 8px rgba(0, 120, 212, 0.2)" // Subtle shadow for selected item
                        : "0 4px 8px rgba(0, 0, 0, 0.1)", // Default shadow for unselected items
                    transition: "all 0.2s ease-in-out", // Smooth transition for hover and selection effects
                    cursor: "pointer", // Pointer cursor to indicate clickability
                    position: "relative"
                }}
                role="row"
                //className={getAssetCssClassNames(asset, selectedAsset)}
                //onClick={() => this.props.loadAdHocOcrProject(this.props.adHocProject.project.id)}
                //onClick={this.loadAdHocProjce}
            >
                {this.props && this.props.adHocProject &&
                    <React.Fragment>
                        <style>
                            {`
                            .asset-filename {
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                display: block;
                                max-width: 200px; /* Adjust the width as needed */
                            }
                            `}
                        </style>
                        <Checkbox  
                            name="select-project-to-post-checkbox"
                            styles={{
                                root: {
                                    position: "absolute", // Position the checkbox absolutely
                                    top: "17px", // Adjust distance from the top
                                    left: "23px", // Adjust distance from the left
                                    transform: "scale(0.8)", // Scale down the checkbox to make it smaller
                                    zIndex: 10, // Ensure it appears above other content
                                    //color: "#333"
                                }                                    
                            }}
                            className="ms-Checkbox-checkbox2"     
                            onChange={(e) => this.selectProjectToPost(e, asset.name) }
                            checked={this.state.isChecked}
                        />
                        <div 
                            className={isSelected ? "asset-item-image-selected" : "asset-item-image"}
                            role="gridcell"
                            onClick={() => this.props.loadAdHocOcrProject(this.props.adHocProject.project.id)}
                            style={{                                
                                backgroundColor: isSelected ? "#0078d4" : "#cce7ff", // Blue background for selected and light blue for unselected
                                color: isSelected ? "#ffffff" : "#004578", // White text for selected, dark blue for unselected
                                //padding: "10px",
                                borderRadius: "5px", // Rounded corners for the asset preview
                                textAlign: "center",
                                //fontWeight: isSelected ? "bold" : "normal", // Bold text for selected item
                                transition: "all 0.2s ease-in-out", // Smooth transition for hover and selection effects
                            }}
                        >                      
                            {/* <Checkbox 
                                styles={{
                                    root: {
                                        position: "absolute", // Position the checkbox absolutely
                                        top: "5px", // Adjust distance from the top
                                        left: "5px", // Adjust distance from the left
                                        transform: "scale(0.8)", // Scale down the checkbox to make it smaller
                                        zIndex: 10, // Ensure it appears above other content
                                        //color: "#333"
                                    }                                    
                                }}          
                                className="ms-Checkbox-checkbox2"
                                onChange={this.selectProjectToPost}
                            />     */}
                            <AssetPreview
                                project={this.props.adHocProject.project}
                            />
                        </div>
                        <div className="asset-item-metadata" role="rowheader" onClick={() => this.props.loadAdHocOcrProject(this.props.adHocProject.project.id)}>
                            <span className="asset-filename" title={asset.name}>
                                {asset.name}
                            </span>                            
                        </div>
                        <div 
                            style={{
                                display: "flex",
                                justifyContent: "center", // Center horizontally
                                alignItems: "center", // Center vertically
                                height: "100%", // Optional: Ensure the container has height
                            }}>                            
                            {/*<PrimaryButton 
                                text="Post to Unity" 
                                styles={{
                                    root: {
                                      height: 24,
                                      padding: '0 8px',
                                      fontSize: 12,
                                    },
                                }}
                            />*/}     
                            <Checkbox
                                label="Generate Model"
                                //onChange={this.onSplitMultiplePageFilesCheckChanged}
                                checked={!cbTitle ? this.props.isGenerateModelChecked : false}
                                style={{
                                    position: "absolute",
                                    bottom: "10px",
                                    right: "10px",
                                    zIndex: 10,
                                    color: isSelected ? "#0078d4" : "#333", // Blue text for selected, dark gray for unselected
                                    fontSize: "12px", // Slightly larger font for better visibility
                                }}
                                className="ms-Checkbox-checkbox2"
                                onChange={(ev: ChangeEvent<HTMLInputElement>) => this.props.onGenerateModelChanged(this.props.adHocProject.vendor, this.props.adHocProject.project.id, ev.target.checked)}
                                name="generate-model"
                                disabled={!!cbTitle ? true : false}
                                title={cbTitle}                            
                            />                       
                        </div>
                        
                    </React.Fragment>
                }
            </div>
        );
    }
}
